/**
 * O que o AuthContext do mobile carregava para a paciente: a gestação
 * corrente, o status e a fase derivada. Fica por cima da sessão do app; a
 * casca já garantiu conta e contexto.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useReadySession } from '../session/SessionProvider';
import { profileApi } from './profile.api';
import { pregnancyApi } from './pregnancy.api';
import { careApi } from './care.api';
import { chatApi } from '../chat/chat.api';
import { useBrand } from '../brand/BrandProvider';
import { computeGestationInfo, type GestationInfo } from './gestation';
import { NON_TEMPORAL_USER_TYPES, phaseLabel, resolvePhaseForUser, type MaternityPhase } from './maternityPhase';
import { avaliarAtencao, notificacoesDaPaciente, type Atencao, type NotificacaoDaPaciente } from './careReminders';
import type { PatientUser, Pregnancy, PregnancyStatus } from './types';

export interface PatientValue {
  user: PatientUser;
  pregnancy: Pregnancy | null;
  pregnancyStatus: PregnancyStatus | null;
  gestation: GestationInfo;
  phase: MaternityPhase;
  /** Dias desde o parto, quando a gestação está concluída com data. */
  diasPosParto: number | null;
  phaseLabel: string;
  isFatherFigure: boolean;
  fatherFigureFor: string | null;
  /** Tentante, perdi meu bebê, adotante, mãe — fases que não evoluem no tempo. */
  isNonTemporal: boolean;
  isPostpartum: boolean;
  isLoading: boolean;
  /** Recarrega usuária e gestação (depois de trocar de jornada, por ex.). */
  reload: () => Promise<void>;
  /** Invalida as consultas de cuidado (aba, banner, bolinha). */
  invalidarCuidado: () => void;
}

const Ctx = createContext<PatientValue | null>(null);

export const CHAVE_ATENCAO = ['atencao'] as const;

export function PatientProvider({ children }: { children: ReactNode }) {
  const s = useReadySession();
  const qc = useQueryClient();

  const usuario = useQuery({
    queryKey: ['patient', 'me'],
    queryFn: profileApi.me,
    staleTime: 5 * 60 * 1000,
    initialData: s.user as unknown as PatientUser,
  });
  const user = (usuario.data ?? (s.user as unknown as PatientUser)) as PatientUser;

  const gestacao = useQuery({
    queryKey: ['patient', 'pregnancy', user.id, user.currentPregnancyId ?? null],
    queryFn: () => (user.role === 'PROVIDER' ? null : pregnancyApi.getCurrent(user)),
    staleTime: 60 * 1000,
  });
  const pregnancy = gestacao.data ?? null;
  const pregnancyStatus = pregnancy?.status ?? null;

  const isFatherFigure = user.userType === 'FATHER_FIGURE';
  const fatherFigureFor = user.fatherFigureFor ?? null;
  const { phase, diasPosParto } = useMemo(() => resolvePhaseForUser(user.userType, pregnancy, fatherFigureFor), [user.userType, pregnancy, fatherFigureFor]);
  const gestation = useMemo(() => computeGestationInfo(pregnancy), [pregnancy]);
  const isNonTemporal = (NON_TEMPORAL_USER_TYPES as readonly string[]).includes(user.userType ?? '') || (isFatherFigure && (NON_TEMPORAL_USER_TYPES as readonly string[]).includes(fatherFigureFor ?? ''));
  const isPostpartum = pregnancyStatus === 'completed' || (isFatherFigure && fatherFigureFor === 'POSTPARTUM');

  const reload = useCallback(async () => {
    const atual = await profileApi.me();
    qc.setQueryData(['patient', 'me'], atual);
    s.setUser({ ...s.user, ...atual } as any);
    await qc.invalidateQueries({ queryKey: ['patient', 'pregnancy'] });
  }, [qc, s]);

  const invalidarCuidado = useCallback(() => {
    for (const k of [CHAVE_ATENCAO, ['care-pending'], ['care-reminders'], ['care-tools'], ['care-state'], ['inspiracao'], ['partner-feed'], ['message-of-day'], ['comunicados']]) qc.invalidateQueries({ queryKey: k as any });
  }, [qc]);

  const value: PatientValue = {
    user, pregnancy, pregnancyStatus, gestation, phase, diasPosParto,
    phaseLabel: phaseLabel(phase, isFatherFigure),
    isFatherFigure, fatherFigureFor, isNonTemporal, isPostpartum,
    isLoading: usuario.isLoading || gestacao.isLoading,
    reload, invalidarCuidado,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePatient(): PatientValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePatient precisa de <PatientProvider>');
  return v;
}

/**
 * O que está esperando a usuária, cru: conversas, lembretes e cuidado
 * pendente. Uma consulta só, a cada minuto, para as bolinhas das abas e para
 * o sino do cabeçalho.
 */
function useEsperando() {
  const { user } = usePatient();
  // Marca sem chat (A053) não consulta conversa a cada minuto só para não
  // acender bolinha nenhuma; sem jornadas (A043), nem cuidado. A chave carrega
  // os interruptores para a resposta de antes não valer depois de ligar ou
  // desligar.
  const { hasFeature } = useBrand();
  const temChat = hasFeature('CHAT');
  const temCuidado = hasFeature('CARE_JOURNEYS');
  const { data } = useQuery({
    queryKey: [...CHAVE_ATENCAO, temChat, temCuidado],
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async () => {
      const [conversas, lembretes, pendentes] = await Promise.all([
        temChat
          ? chatApi.listConversations().catch(() => ({ items: [] as any[] }))
          : Promise.resolve({ items: [] as any[] }),
        careApi.reminders().catch(() => []),
        temCuidado ? careApi.pending().then((p) => p.items).catch(() => []) : Promise.resolve([]),
      ]);
      return { conversas: conversas.items ?? [], lembretes: lembretes ?? [], pendentes };
    },
  });
  return { dados: data, userId: user.id };
}

/** Há algo esperando a usuária? `chat` = mensagem não lida; `cuidado` = lembrete aberto ou checkpoint pendente. */
export function useAtencao(): Atencao {
  const { dados, userId } = useEsperando();
  const { chat, cuidado } = dados ? avaliarAtencao(dados.conversas, dados.lembretes, userId, new Date(), dados.pendentes) : { chat: false, cuidado: false };
  // O mesmo objeto enquanto os dois sinais não mudam. A consulta traz as
  // listas cruas, que mudam a cada leitura (a hora de leitura da conversa);
  // um objeto novo a cada vez redesenhava o shell, que remontava a conversa
  // aberta — ela recarregava, marcava como lida, invalidava a atenção, e o
  // ciclo não parava (visto em 14/10/2026, com a conversa presa no carregando).
  return useMemo(() => ({ chat, cuidado }), [chat, cuidado]);
}

/** O sino do cabeçalho (item 9): a lista do que está esperando e quanto soma no selo. */
export function useNotificacoes(): { itens: NotificacaoDaPaciente[]; total: number; carregando: boolean } {
  const { dados, userId } = useEsperando();
  return useMemo(() => {
    if (!dados) return { itens: [], total: 0, carregando: true };
    const itens = notificacoesDaPaciente(dados.conversas, dados.lembretes, userId, new Date(), dados.pendentes);
    return { itens, total: itens.reduce((t, n) => t + n.quantidade, 0), carregando: false };
  }, [dados, userId]);
}
